import {
  AllCommentsProps,
  BusniessOfficeNote,
} from "../../../data-model/busniessOfficeNote";
import { BusniessOfficeReportType } from "../../../enums/busniessOfficeReportType";
import { checkIfDatesAreSame } from "../../../utils/formatDate";

/**
 * Creates a consolidated array of business office notes data by aggregating
 * post notes, pre notes, and visit data into a single structure.
 * Filters out any comment groups that have no comments.
 *
 * @param {BusniessOfficeNote[]} [notes] - Array of business office notes to process.
 * @param {string} [templateText] - Optional template text to override the default write-back comment.
 * @param {string} [dateOfService] - The date of service to compare against note dates.
 * @returns {AllCommentsProps[]} Array of comment groups (post, pre, visit) that have at least one comment.
 * @example
 * const notesData = createBusniessOfficeNotesData(notes, "Template", "2024-01-15");
 * // Returns array of { comments, commentDate, type } objects with non-empty comments
 */
export const createBusniessOfficeNotesData = (
  notes?: BusniessOfficeNote[],
  templateText?: string,
  dateOfService?: string,
): AllCommentsProps[] => {
  const postNotes = createBusniessOfficePostNotes(
    notes,
    templateText,
    dateOfService,
  );
  const preNotes = createBusniessOfficePreNotes(notes);
  const visitData = createBusniessOfficeVisitData(notes, dateOfService);
  const allComments = [postNotes, preNotes, visitData];

  return allComments.filter((comment) => comment.comments.length > 0);
};

/**
 * Creates business office post notes data structure from raw notes.
 * Maps each note to a comment object with write-back functionality enabled
 * when the post text date matches the date of service.
 *
 * @param {BusniessOfficeNote[]} [notes] - Array of business office notes to process.
 * @param {string} [templateText] - Optional template text to use as the write-back comment (overrides note's template_text).
 * @param {string} [dateOfService] - The date of service to compare against post text dates.
 * @returns {AllCommentsProps} Object containing comments array, comment date, and type (FINAL if dates match, otherwise POST).
 * @example
 * const postNotes = createBusniessOfficePostNotes(notes, "Template", "2024-01-15");
 * // Returns: { comments: [...], commentDate: "...", type: BusniessOfficeReportType.FINAL | POST }
 */
export const createBusniessOfficePostNotes = (
  notes?: BusniessOfficeNote[],
  templateText?: string,
  dateOfService?: string,
): AllCommentsProps => {
  const postNotesData = notes?.map((note) => {
    let writeBackComment = note.template_text;
    if (templateText) {
      writeBackComment = templateText;
    }
    return {
      id: note.report_identifier ?? "",
      originalComment:
        note.post_text === ""
          ? "No comments found on OncoEmr for this order"
          : (note.post_text ?? ""),
      boStatus: note.report_status ?? "",
      showWriteBackNote: checkIfDatesAreSame(
        note.post_text_date,
        dateOfService,
      ),
      writeBackComment: writeBackComment,
    };
  });

  return {
    comments: postNotesData ?? [],
    commentDate: notes?.[0]?.post_text_date ?? "",
    type: checkIfDatesAreSame(notes?.[0]?.post_text_date, dateOfService)
      ? BusniessOfficeReportType.FINAL
      : BusniessOfficeReportType.POST,
  };
};

/**
 * Creates business office pre notes data structure from raw notes.
 * Filters notes to include only those with non-empty pre_text or pre_text_date,
 * then maps them to a simplified comment format without write-back functionality.
 *
 * @param {BusniessOfficeNote[]} [notes] - Array of business office notes to process.
 * @returns {AllCommentsProps} Object containing filtered pre-notes comments, date, and PRE type.
 *                             Returns empty comments array if no valid pre-notes exist.
 * @example
 * const preNotes = createBusniessOfficePreNotes(notes);
 * // Returns: { comments: [...], commentDate: "...", type: BusniessOfficeReportType.PRE }
 */
const createBusniessOfficePreNotes = (
  notes?: BusniessOfficeNote[],
): AllCommentsProps => {
  const preNotes = notes?.filter(
    (note) =>
      (note?.pre_text && note.pre_text.trim()) ||
      (note?.pre_text_date && note.pre_text_date.trim()),
  );
  if (preNotes?.length === 0) {
    return {
      comments: [],
      commentDate: "",
      type: BusniessOfficeReportType.PRE,
    };
  }

  const preNotesData = preNotes?.map((note) => {
    return {
      id: note.report_identifier ?? "",
      originalComment: note.pre_text ?? "",
      boStatus: "",
      showWriteBackNote: false,
    };
  });

  return {
    comments: preNotesData?.filter((note) => note.originalComment !== "") ?? [],
    commentDate: preNotes?.[0]?.pre_text_date ?? "",
    type: BusniessOfficeReportType.PRE,
  };
};

/**
 * Creates business office visit data structure from raw notes.
 * Filters notes to include only those with non-empty visit_date or visit_date_text,
 * and excludes notes where visit date matches post text date.
 * Write-back is enabled when visit date matches the date of service.
 *
 * @param {BusniessOfficeNote[]} [notes] - Array of business office notes to process.
 * @param {string} [dateOfService] - The date of service to compare against visit dates.
 * @returns {AllCommentsProps} Object containing visit comments, date, and type.
 *                             Type is FINAL if visit date matches date of service and differs from post text date,
 *                             otherwise VISIT. Returns empty comments array if no valid visit data exists.
 * @example
 * const visitData = createBusniessOfficeVisitData(notes, "2024-01-15");
 * // Returns: { comments: [...], commentDate: "...", type: BusniessOfficeReportType.FINAL | VISIT }
 */
const createBusniessOfficeVisitData = (
  notes?: BusniessOfficeNote[],
  dateOfService?: string,
) => {
  const visitData = notes?.filter(
    (note) =>
      (note?.visit_date && note.visit_date.trim()) ||
      (note?.visit_date_text && note.visit_date_text.trim()),
  );

  if (visitData?.length === 0) {
    return {
      comments: [],
      commentDate: "",
      type: BusniessOfficeReportType.VISIT,
    };
  }

  const visitDataMap = visitData?.map((note) => {
    let writeBackComment = note?.template_text ?? "";
    return {
      id: note.report_identifier ?? "",
      originalComment:
        note.visit_date_text === ""
          ? "No comments found on OncoEmr for this order"
          : (note.visit_date_text ?? ""),
      boStatus: note?.report_status ?? "",
      showWriteBackNote: checkIfDatesAreSame(note.visit_date, dateOfService),
      isVisitDateDifferentFromPostTextDate:
        note?.visit_date !== note?.post_text_date,
      writeBackComment: writeBackComment,
    };
  });

  const checkIfVisitDateAndDateOfServiceAreSame = checkIfDatesAreSame(
    visitData?.[0]?.visit_date,
    dateOfService,
  );
  const checkIfPostTextDateAndVisitDateAreSame = checkIfDatesAreSame(
    visitData?.[0]?.post_text_date,
    visitData?.[0]?.visit_date,
  );
  return {
    comments:
      visitDataMap?.filter(
        (note) =>
          note.originalComment !== "" &&
          note.isVisitDateDifferentFromPostTextDate,
      ) ?? [],
    commentDate: visitData?.[0]?.visit_date ?? "",
    type:
      checkIfVisitDateAndDateOfServiceAreSame &&
      !checkIfPostTextDateAndVisitDateAreSame
        ? BusniessOfficeReportType.FINAL
        : BusniessOfficeReportType.VISIT,
  };
};
